import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, Settings, Play, Clock, ShieldAlert, Users, Target, UserPlus, Clipboard, Mail, Globe, StickyNote, Tag, List } from 'lucide-react';

const NodeWrapper = ({ title, icon: Icon, children, colorClass, type, isSelected }) => (
    <div className={`group px-4 py-3 rounded-2xl shadow-xl transition-all border-2 bg-card/90 backdrop-blur-sm ${isSelected ? 'border-primary ring-4 ring-primary/10 shadow-primary/20' : 'border-border'} w-64`}>
        <div className="flex items-center gap-3 mb-2 pb-2 border-b border-border/50">
            <div className={`p-2 rounded-xl ${colorClass} shadow-sm group-hover:scale-110 transition-transform`}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60 leading-none">{type}</span>
                <h4 className="text-sm font-bold text-foreground truncate leading-tight mt-0.5">{title}</h4>
            </div>
        </div>
        <div className="text-[11px] text-muted-foreground/80 leading-relaxed min-h-[40px]">
            {children}
        </div>
    </div>
);

export const TriggerNode = memo(({ data, selected }) => {
    const triggerInfo = {
        REPLY: { icon: Zap, color: 'bg-blue-500/10 text-blue-500', label: 'Inbound Reply' },
        NO_REPLY: { icon: Clock, color: 'bg-indigo-500/10 text-indigo-500', label: 'No Reply Received' },
        BOUNCE: { icon: ShieldAlert, color: 'bg-red-500/10 text-red-500', label: 'Email Bounced' },
        AUTO_REPLY: { icon: UserPlus, color: 'bg-teal-500/10 text-teal-500', label: 'Auto-Reply' }
    };
    
    const info = triggerInfo[data.triggerType] || { icon: Zap, color: 'bg-blue-500/10 text-blue-500', label: 'Trigger' };
    const Icon = info.icon;
    
    return (
        <NodeWrapper title={data.label || info.label} icon={Icon} colorClass={info.color} type="Trigger Node" isSelected={selected}>
            <p className="line-clamp-2 italic">{data.description || `Starts when a ${info.label.toLowerCase()} is detected.`}</p>
            <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-primary border-4 border-card shadow-sm !-bottom-1.5" />
        </NodeWrapper>
    );
});

export const ConditionNode = memo(({ data, selected }) => {
    return (
        <NodeWrapper title={data.label || 'Filter Condition'} icon={Target} colorClass="bg-amber-500/10 text-amber-500" type="Condition Node" isSelected={selected}>
            <div className="line-clamp-3 font-medium text-foreground/70 mb-4 text-[10px] leading-relaxed">
                {data.summary || (data.params?.conditions?.length > 0 
                    ? `${data.params.conditions.length} filters (${data.params.logicalOperator || 'AND'})`
                    : 'Branching logic based on conditions.')}
            </div>
            <Handle type="target" position={Position.Top} className="w-3 h-3 bg-primary border-4 border-card !-top-1.5" />
            <div className="flex flex-col gap-2 mt-3 pt-2 border-t border-border/50">
                {/* Success Branch */}
                <div className="flex items-center gap-2 group/branch">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]" />
                    <span className="text-[9px] font-black uppercase tracking-tighter text-green-600/80 group-hover/branch:text-green-600 transition-colors">Matched</span>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="yes"
                        style={{ top: '68%' }}
                        className="!w-3 !h-3 !bg-green-500 !border-2 !border-background !-right-1.5 hover:!scale-125 !z-10 transition-transform shadow-lg"
                    />
                </div>
                {/* Failure Branch */}
                <div className="flex items-center gap-2 group/branch">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]" />
                    <span className="text-[9px] font-black uppercase tracking-tighter text-red-600/80 group-hover/branch:text-red-600 transition-colors">Not Matched</span>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="no"
                        style={{ top: '85%' }}
                        className="!w-3 !h-3 !bg-red-500 !border-2 !border-background !-right-1.5 hover:!scale-125 transition-transform shadow-lg"
                    />
                </div>
            </div>
        </NodeWrapper>
    );
});

export const ActionNode = memo(({ data, selected }) => {
    const actionTypes = {
        TASK: { 
            icon: Clipboard, 
            label: 'Create Task', 
            desc: 'Creates a workspace follow-up task.' 
        },
        SEND_EMAIL: { 
            icon: Mail, 
            label: 'Send Email', 
            desc: 'Sends a semi-automated email reply.' 
        },
        REPLY: { 
            icon: Mail, 
            label: 'Send Auto-Reply', 
            desc: 'Sends a reactive email reply.' 
        },
        WEBHOOK: { 
            icon: Globe, 
            label: 'Trigger Webhook', 
            desc: 'Broadcasts data to an external URL.' 
        },
        SEQUENCE_ENROLLMENT: { 
            icon: List, 
            label: 'Enroll in Sequence', 
            desc: 'Starts a sequence for the contact.' 
        },
        ASSIGN_OWNER: { 
            icon: Users, 
            label: 'Assign Owner', 
            desc: 'Reassigns the contact ownership.' 
        },
        ADD_NOTE: { 
            icon: StickyNote, 
            label: 'Add Note', 
            desc: 'Appends an internal CRM note.' 
        },
        STATUS_CHANGE: { 
            icon: Settings, 
            label: 'Change Status', 
            desc: 'Updates the contact lifecycle stage.' 
        },
        TAG: { 
            icon: Tag, 
            label: 'Add Tag', 
            desc: 'Appends a label/tag to contact.' 
        },
        SUPPRESS: { 
            icon: ShieldAlert, 
            label: 'Add to Blocklist', 
            desc: 'Prevents future outreach.' 
        }
    };
    
    const info = actionTypes[data.actionType] || { icon: Play, label: 'Action', desc: 'Performs an automated action.' };
    const Icon = info.icon;

    return (
        <NodeWrapper title={data.label || info.label} icon={Icon} colorClass="bg-green-500/10 text-green-500" type="Action Node" isSelected={selected}>
            <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground/80 italic leading-tight">
                    {data.description || info.desc}
                </p>
                {data.summary && (
                    <div className="mt-2 pt-2 border-t border-border/40">
                        <p className="text-[10px] font-black uppercase tracking-tighter text-foreground/90 whitespace-pre-wrap leading-relaxed">
                            {data.summary}
                        </p>
                    </div>
                )}
            </div>
            <Handle type="target" position={Position.Top} className="w-3 h-3 bg-primary border-4 border-card shadow-sm !-top-1.5" />
            <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-primary border-4 border-card shadow-sm !-bottom-1.5" />
        </NodeWrapper>
    );
});
